<template>
    <div class="login-page">

        <Panel>
            <div class="login-form">
                <div v-if="isLoading" class="text-center">
                    <Loader wait-text="Processing..."/>
                </div>

                <div v-else>
                    <h1 v-if="app.identifier">Log into {{ app.name }}</h1>

                    <div>
                        <Alert type="danger" :message="errorMessage"/>
                    </div>

                    <form @submit.prevent="handleLogin">
                        <div class="form-group">
                            <label>Username</label>
                            <input type="text" v-model="form.username"
                                   placeholder="Username"
                                   required class="form-control">
                        </div>

                        <div class="form-group" v-if="!isAzureAdUser">
                            <label>Password</label>
                            <input :type="showPassword ? 'text' : 'password'" v-model="form.password"
                                   placeholder="Password"
                                   class="form-control">

                            <i class="fas toggle-password-eye" :class="showPassword ? 'fa-eye' : 'fa-eye-slash'"
                               @click="changeShowHide()"></i>
                        </div>

                        <div class="form-group" v-if="isAzureAdUser">
                            Please click on "Login" to login with your {{ azureAdName }} credentials.
                        </div>

                        <div class="captcha" v-if="captchaRequired">
                            <div>Please enter the text from image below:</div>
                            <img :src="`/site/captcha?attempt=${attemptTimestamp}`" alt="Captcha"/>

                            <div>
                                <div class="form-group">
                                    <label>Captcha</label>
                                    <input type="text" v-model="form.captcha"
                                           placeholder="Captcha text"
                                           required class="form-control"
                                    >
                                </div>
                            </div>
                        </div>

                        <div class="reset-password">
                            <div class="row">
                                <div class="col-md-6 text-left">
                                    <a href="javascript:void(0)" v-if="isEmsAzureAdTenant" @click="handleResetPassword()">
                                        Reset your password
                                    </a>
                                </div>
                                <div class="col-md-6 text-right" v-if="!isAzureAdUser">
                                    <a :href="azureLoginLink">{{ azureAdName }} Login</a>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-6 text-left">
                                <br/>

                                <a v-if="isEmsAzureAdTenant" href="/documents/EMSight-Password-Reset-Guide-v2.pdf" target="_blank">
                                    Need Help?
                                </a>
                            </div>
                            <div class="col-md-6 text-right login-buttons">
                                <button type="submit" class="btn btn-primary btn-ems" :disabled="v$.$invalid">
                                    Login
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Panel>
    </div>
</template>

<script>
import Alert from "@/components/Alert.vue";
import Panel from "@/components/Panel";
import { useLoading } from "@/hooks/use-loading";
import { showHidePassword } from "@/hooks/show-hide-password";
import { useForm } from "@/hooks/use-form";
import { required, requiredIf } from 'vuelidate/lib/validators';
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { applicationState } from '@/stores/application';
import { useRoute, useRouter } from "vue-router";
import { goToAzureAdPasswordReset, isAzureAdEmail, TENANT_EMSMC } from "@/lib/azure";
import { setupAzureConfig, state as azureAd } from "@/stores/azure-ad";
import { sendPost } from "@/api/base";
import Loader from "@/components/Loader";

export default {
    name: 'LoginPage',
    components: {
        Loader,
        Panel,
        Alert
    },
    setup() {
        const { showPassword, changeShowHide } = showHidePassword(false);
        const { isLoading, startLoading, stopLoading } = useLoading(false);
        const route = useRoute();
        const errorMessage = ref('');
        const attemptTimestamp = ref(0);
        const captchaRequired = ref(false);
        const router = useRouter();
        const { form, v$ } = useForm({
            props: {
                username: '',
                password: '',
                captcha: '',
            },
            rules: {
                username: { required },
                password: { requiredIf: requiredIf(() => !isAzureAdUser.value) },
                captcha: { requiredIf: requiredIf(() => captchaRequired.value) },
            }
        });

        const isAzureAdUser = computed(() => isAzureAdEmail(form.username));

        const isEmsAzureAdTenant = computed(() => azureAd.key === TENANT_EMSMC);

        const getLoginParams = (additionalParams = {}) => {
            const finalParams = { ...additionalParams };

            if (applicationState.identifier) {
                finalParams.appId = applicationState.identifier;
            }

            if (applicationState.returnUrl) {
                finalParams.returnUrl = applicationState.returnUrl;
            }

            return new URLSearchParams(finalParams);
        };

        const handleLogin = async () => {
            if (v$.value.$invalid && !isAzureAdUser.value) {
                return;
            }

            if (isAzureAdUser.value) {
                startLoading();
                const { data } = await sendRequestAuthorizeUrl(form.username);
                window.location.href = data.result;
                return;
            }

            errorMessage.value = '';
            startLoading();
            try {
                attemptTimestamp.value = (new Date()).getTime();
                await sendForm();

                const params = getLoginParams();

                window.location.href = `/?${params}`;
            } catch (e) {
                if (e.response) {
                    errorMessage.value = e.response.data.message;

                    if (e.response.data.captcha_required) {
                        captchaRequired.value = true;
                    }
                }

                stopLoading();
            }
        };

        const handleResetPassword = () => {
            if (isAzureAdUser.value) {
                goToAzureAdPasswordReset();
                return;
            }
            router.push('/reset-request');
        };

        const sendForm = () => sendPost('/login/user', form);

        const sendRequestAuthorizeUrl = (email) => sendPost(`${azureAd.authorizeUrl}?${getLoginParams()}`, { email });

        const azureLoginLink = computed(() => {
            const urlParams = getLoginParams()
            return `${azureAd.loginUrl}?${urlParams}`;
        });

        onBeforeMount(() => setupAzureConfig({ tenant: route.params.tenant }))

        onMounted(() => errorMessage.value = route.query.loginError ? route.query.loginError : '');

        return {
            app: applicationState,
            attemptTimestamp,
            captchaRequired,
            azureLoginLink,
            azureAdName: computed(() => azureAd.name),
            form,
            v$,
            errorMessage,
            isLoading,
            isAzureAdUser,
            isEmsAzureAdTenant,
            handleLogin,
            handleResetPassword,
            showPassword,
            changeShowHide,
        };
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.login-page {
    max-width: 500px;
    margin: 10% auto 0 auto;

    h1 {
        font-size: 20px;
        padding-bottom: 20px;
    }

    .login-buttons {
        padding-top: 10px;
    }

    .captcha {
        margin: 10px 0;
    }
}
</style>